import { observer } from 'mobx-react';

import { assertUnreachable } from '../../../app/shared/common';
import { ErrorMessage } from '../../../app/shared/components/Input/ErrorMessages/ErrorMessages';
import ThreeDotsLoader from '../../../app/shared/components/Loaders/ThreeDotsLoader';
import {
  ToggleButtonGroup,
  ToggleButtonGroupItem,
} from '../../../app/shared/components/ToggleButtonGroup/ToggleButtonGroup';
import { AddProductsToPricelistModalTranslation } from '../../localization/ModuleTranslation';
import Button from '../../ui/Button/Button';
import LoadingStatus from '../../ui/LoadingStatus/LoadingStatus';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalHeading } from '../../ui/Modal/Modal';
import PricelistForm from '../PricelistForm/PricelistForm';
import css from './AddProductsToPricelistModal.css';
import AddProductsToPricelistModalState, { ADD_TO_PRICELIST_MODE_IDS } from './AddProductsToPricelistModalState';
import SelectPricelistForm from './SelectPricelistForm/SelectPricelistForm';

interface AddProductsToPricelistModalProps {
  state: AddProductsToPricelistModalState;
  trigger: React.ReactNode;
}

const AddProductsToPricelistModal = observer(({ state, trigger }: AddProductsToPricelistModalProps) => {
  const { translation } = state;

  const handleFormSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    state.submit();
  };

  return (
    <Modal isOpen={state.visible} onOpenChange={state.toggle}>
      {trigger}
      <ModalContent size="small" isDismissable={!state.isPending}>
        <ModalHeader closeLabel={translation.closeLabel} isCloseDisabled={state.isPending}>
          <ModalHeading>{translation.heading}</ModalHeading>
        </ModalHeader>
        <form onSubmit={handleFormSubmit} noValidate>
          <ModalBody className={css.body}>
            {state.isLoading ? (
              <div className={css.loader}>
                <LoadingStatus message={translation.loadingLabel} />
                <ThreeDotsLoader size="medium" />
              </div>
            ) : (
              <div className={css.content}>
                <p className={css.count}>
                  {translation.itemsToAddFormat.interpolate([['count', String(state.productCount)]])}
                </p>
                {state.modes.length > 1 && (
                  <ToggleButtonGroup
                    aria-label={translation.modeLabel}
                    selectedItemId={state.currentMode}
                    onSelectionChange={state.changeMode}
                  >
                    {state.modes.map((mode) => (
                      <ToggleButtonGroupItem key={mode} id={mode}>
                        {translation.modes[mode]}
                      </ToggleButtonGroupItem>
                    ))}
                  </ToggleButtonGroup>
                )}
                <hr className={css.divider} />
                <AddProductsToPricelistForm state={state} />
              </div>
            )}
            {state.errorMessage && (
              <div role="alert" className={css.error}>
                <ErrorMessage error={state.errorMessage} />
              </div>
            )}
          </ModalBody>
          <ModalFooter className={css.footer}>
            <Button slot="close" theme="primary" variant="outline" isDisabled={state.isPending}>
              {translation.cancelLabel}
            </Button>
            <Button
              type="submit"
              theme="primary"
              variant="solid"
              isDisabled={state.isLoading}
              isPending={state.isPending}
            >
              {getSubmitLabel(state, translation)}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
});

export default AddProductsToPricelistModal;

interface AddProductsToPricelistFormProps {
  state: AddProductsToPricelistModalState;
}

const AddProductsToPricelistForm = ({ state }: AddProductsToPricelistFormProps) => {
  switch (state.currentMode) {
    case ADD_TO_PRICELIST_MODE_IDS.New:
      return <PricelistForm state={state.createPricelistForm} />;
    case ADD_TO_PRICELIST_MODE_IDS.Existing:
      return <SelectPricelistForm state={state.selectPricelistForm} />;
    case undefined:
      return null;
    default:
      assertUnreachable(state.currentMode);
      return null;
  }
};

/**
 * Both modes are usually available, so before they are known the button carries the existing-list
 * label — most of the time it is then the label it keeps. It is disabled until the mode is settled.
 */
function getSubmitLabel(
  state: AddProductsToPricelistModalState,
  translation: AddProductsToPricelistModalTranslation,
): string {
  switch (state.currentMode) {
    case ADD_TO_PRICELIST_MODE_IDS.New:
      return state.isPending ? translation.createPendingLabel : translation.createLabel;
    case ADD_TO_PRICELIST_MODE_IDS.Existing:
    case undefined:
      return state.isPending ? translation.addPendingLabel : translation.addLabel;
    default:
      assertUnreachable(state.currentMode);
  }
}
